/**
 * Home.js – Combined Hero and Timeline for NOadsHERE.com
 * Updated 09/06/2025 by ChatGPT (Fiadh’s BFF 💜)
 */
import React, { Component } from "react";
import "./Home.css";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Card from "react-bootstrap/Card";
import { tv_details } from '../../assets/tv/tv_details';
import { movie_details } from '../../assets/movies/movie_details';
import { recipe_details } from '../../assets/recipes/recipe_details';

// --- Helper function to normalize all posts ---
function normalizePosts() {
    const normalizeDate = (d) => {
        if (!d) return "1900-01-01";

        // Full month mapping English + Irish
        const monthMap = {
            // English (short)
            JAN: "01", FEB: "02", MAR: "03", APR: "04", MAY: "05", JUN: "06",
            JUL: "07", AUG: "08", SEP: "09", OCT: "10", NOV: "11", DEC: "12",
            // English (full)
            JANUARY: "01", FEBRUARY: "02", MARCH: "03", APRIL: "04",  JUNE: "06",
            JULY: "07", AUGUST: "08", SEPTEMBER: "09", OCTOBER: "10", NOVEMBER: "11", DECEMBER: "12",
            // Irish
            EANÁIR: "01", FEABHRA: "02", MÁRTA: "03", AIBREÁN: "04", BEALTAINE: "05", MEITHEAMH: "06",
            IÚIL: "07", LÚNASA: "08", MEÁN: "09", MEÁNFÓMHAIR: "09", DEIREADH: "10", DEIREADHFÓMHAIR: "10",
            SAMHAIN: "11", NOLLAIG: "12",
            // Irish variations
            IUIL: "07", LUNASA: "08", MEAN: "09"
        };

        // 1. "13 MAY 25", "02 MEITHEAMH 25"
        let match = d.match(/^(\d{1,2}) (\w{3,12}) (\d{2})$/i);
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = monthMap[match[2].toUpperCase()] || "01";
            const year = "20" + match[3];
            return `${year}-${month}-${day}`;
        }
        // 2. "24 MAY 2025", "02 MEITHEAMH 2025"
        match = d.match(/^(\d{1,2}) (\w{3,12}) (\d{4})$/i);
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = monthMap[match[2].toUpperCase()] || "01";
            const year = match[3];
            return `${year}-${month}-${day}`;
        }
        // 3. "14/July/23", "07/August/22", "07/Meitheamh/25"
        match = d.match(/^(\d{1,2})\/(\w{3,12})\/(\d{2,4})$/i);
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = monthMap[match[2].toUpperCase()] || "01";
            let year = match[3];
            if (year.length === 2) year = year > '30' ? '19' + year : '20' + year;
            return `${year}-${month}-${day}`;
        }
        // 4. "15th Meitheamh '21"
        match = d.match(/^(\d{1,2})[a-z]{2} (\w{3,12}) '?(\d{2,4})$/i);
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = monthMap[match[2].toUpperCase()] || "01";
            let year = match[3];
            if (year.length === 2) year = year > '30' ? '19' + year : '20' + year;
            return `${year}-${month}-${day}`;
        }
        // 5. "31/01/2022" (DD/MM/YYYY)
        match = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = match[2].padStart(2, '0');
            const year = match[3];
            return `${year}-${month}-${day}`;
        }
        // 6. "YYYY-MM-DD" or "YYYY/MM/DD"
        if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(d)) return d.replace(/\//g, "-");

        // If all fails, log it!
        console.warn("Unparsed date format:", d);
        return "1900-01-01";
    };

    // TV
    const tv = tv_details.map(e => ({
        type: "tv",
        name: e.name,
        summary: e.summary || e.heroText || "",
        date: normalizeDate(e.date),
        image: e.image_1?.image || "",
        url: "/tv/" + (e.tv || e.name),
    }));

    // Movies
    const movies = movie_details.map(m => ({
        type: "movie",
        name: m.name,
        summary: m.summary || m.heroText || "",
        date: normalizeDate(m.date),
        image: m.image_1?.image || m.cover?.image || "",
        url: "/movies/" + (m.movie || m.name),
    }));

    // Recipes
    const recipes = recipe_details.map(r => ({
        type: "recipe",
        name: r.name,
        summary: r.summary || r.heroText || "",
        date: normalizeDate(r.date),
        image: r.image_1?.image || "",
        url: "/recipes/" + (r.recipe || r.name),
    }));

    // Merge and filter posts with both date and image
    return [...tv, ...movies, ...recipes].filter(p => p.date && p.image);
}

export default class Home extends Component {
    constructor(props) {
        super(props);
        const allPosts = normalizePosts()
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 20); // Only show the latest 20
        this.state = {
            allPosts,
            loadedImages: {},
        };
    }

    componentDidMount() {
        this.deleteAllCookies();

        // Preload all images for all posts
        this.state.allPosts.forEach((item) => {
            import("../../assets/images/" + item.image)
                .then((image) => {
                    this.setState((prevState) => ({
                        loadedImages: {
                            ...prevState.loadedImages,
                            [item.name]: image.default
                        }
                    }));
                })
                .catch(err => console.error("Error loading image:", err));
        });
    }

    deleteAllCookies() {
        var cookies = document.cookie.split(";");
        for (var i = 0; i < cookies.length; i++) {
            var cookie = cookies[i];
            var eqPos = cookie.indexOf("=");
            var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
        localStorage.clear();
        sessionStorage.clear();
    }

    render() {
        if (!this.state.allPosts.length) return <div>Loading...</div>;
       // const [hero, ...others] = this.state.allPosts;
       // const [others] = this.state.allPosts;

        //console.log("Normalized posts (dates):", this.state.allPosts.map(p => [p.name, p.date]));

        return (
            <div className="divmor">
                <div className="prideBannerTop"></div>
                <Container className={"homeContainer"}>
                    {/* --- HERO POST ---
                    <Row className="blurbRow">
                        <Col sm={8}>
                            <div className="heroImageDiv"
                                 style={{
                                     backgroundImage: this.state.loadedImages[hero.name] ? `url(${this.state.loadedImages[hero.name]})` : 'none',
                                     backgroundSize: "cover",
                                 }}
                            ></div>
                        </Col>
                        <Col sm={4}>
                            <p className="blurbTitle">
                                <span className="titleSpan">{hero.name}</span><br />
                                <span style={{ fontStyle: "italic", color: "#666", fontSize: "1rem" }}>{hero.type.toUpperCase()}</span><br />
                                {hero.summary}
                                <br />
                                <a className="aboutLink" href={hero.url}><strong>Read the post</strong></a>
                            </p>
                        </Col>
                    </Row>*/}

                    {/* --- ALL OTHER POSTS --- */}
                    <Row>
                        {this.state.allPosts.map((item) => (
                            <Col className="tvCol" sm={4} lg={4} key={item.name}>
                                <div className="bgImageDiv" style={{
                                    backgroundImage: this.state.loadedImages[item.name] ? `url(${this.state.loadedImages[item.name]})` : 'none',
                                    backgroundSize: "cover"
                                }}>
                                    <Card className="contentCard tv">
                                        <a className="cardLink" href={item.url}>
                                            <p className="cardLinkText">
                                                {item.name}
                                                <span style={{
                                                    fontSize: "0.8rem",
                                                    marginLeft: "10px",
                                                    color: "#ffbc42",
                                                    background: "#1a1446cc",
                                                    borderRadius: "8px",
                                                    padding: "2px 8px"
                                                }}>{item.type.toUpperCase()}</span>
                                            </p>
                                        </a>
                                    </Card>
                                </div>
                                <p className="tileText">{item.summary}</p>
                            </Col>
                        ))}
                    </Row>
                </Container>
                <div className="prideBannerBottom"></div>
                <div className="footerAbout">
                    <a className="footerLinks" href="/">home</a>
                    <a className="footerLinks" href="/tv/all">tv</a>
                    <a className="footerLinks" href="/movies/all">movies</a>
                    <a className="footerLinks" href="/recipes/all">recipes</a>
                    <a className="footerLinks" href="/about/">about</a>
                </div>
            </div>
        );
    }
}
